import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import SignatureCanvas from 'react-signature-canvas'
import { usePlanEvidenceFiles } from '../../createActionPlanManual/hooks/usePlanEvidenceFiles'
import { UploadedEvidenceFile } from '../../createActionPlanManual/types'
import { EvidencesTypeValueEnum } from '../../../../../utils/enums/evidences'
import { Types } from '../../../../../utils/enums'
import SnackSimple from '../../../../../components/snackbars/simple'
import { icons } from '../../../../../utils/icons'
import { SolverEvidence, SolverEvidenceType } from '../types'
import { isValidEvidenceFile } from '../utils'
import { useStyles } from '../styles'

type ViewMode = 'list' | 'grid'

interface EvidenceTypeConfig {
  accept: Record<string, string[]>
  formats: string
  multiple: boolean
}

const EVIDENCE_TYPE_CONFIG: Record<SolverEvidenceType, EvidenceTypeConfig> = {
  image: {
    accept: { 'image/*': [] },
    formats: 'PNG, JPG, JPEG',
    multiple: true,
  },
  camera: {
    accept: { 'image/*': [] },
    formats: 'PNG, JPG, JPEG',
    multiple: true,
  },
  video: {
    accept: { 'video/*': [] },
    formats: 'MP4, AVI, MOV',
    multiple: true,
  },
  video_gallery: {
    accept: { 'video/*': [] },
    formats: 'MP4, AVI, MOV',
    multiple: true,
  },
  sign: {
    accept: { 'image/*': [] },
    formats: 'PNG, JPG, JPEG',
    multiple: true,
  },
  document: {
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': [],
      'text/plain': [],
      'text/csv': [],
    },
    formats: 'XLSX, PDF, TXT, DOCX, PPTX y CSV',
    multiple: true,
  },
  comment: {
    accept: {},
    formats: '',
    multiple: false,
  },
}

const cleanFileName = (fileName: string) => fileName.replace(/\s+/g, '')

const isImageEvidence = (evidenceTypeId: number) =>
  evidenceTypeId === EvidencesTypeValueEnum.IMAGE ||
  evidenceTypeId === EvidencesTypeValueEnum.CAMERA ||
  evidenceTypeId === EvidencesTypeValueEnum.SIGN

const isVideoEvidence = (evidenceTypeId: number) =>
  evidenceTypeId === EvidencesTypeValueEnum.VIDEO ||
  evidenceTypeId === EvidencesTypeValueEnum.VIDEO_GALLERY

const isDocumentEvidence = (evidenceTypeId: number) =>
  evidenceTypeId === EvidencesTypeValueEnum.DOCUMENT

interface EvidenceUploaderProps {
  planId?: number
  evidences: SolverEvidence[]
  onChange: (evidences: SolverEvidence[]) => void
}

const EvidenceUploader = ({ planId, evidences, onChange }: EvidenceUploaderProps) => {
  const { t } = useTranslation()
  const styles = useStyles()

  const { uploadFile, deleteFile } = usePlanEvidenceFiles()
  const uploadNameRef = useRef(`solve-action-plan/${planId ?? Date.now()}`)
  const uploadPromisesRef = useRef<Promise<unknown>[]>([])

  const [localEvidences, setLocalEvidences] = useState<SolverEvidence[]>(evidences)
  const [selectedTypeId, setSelectedTypeId] = useState<SolverEvidenceType>(
    evidences[0]?.id ?? 'image'
  )
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [commentText, setCommentText] = useState('')
  const signRef = useRef<SignatureCanvas | null>(null)
  const [isSignEmpty, setIsSignEmpty] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'error' as Types,
  })

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const showSnackbar = (message: string, type: Types = Types.ERROR) => {
    setSnackbar({ open: true, message, type })
  }

  useEffect(() => {
    setLocalEvidences(evidences)
    if (evidences.length > 0 && !evidences.some((e) => e.id === selectedTypeId)) {
      setSelectedTypeId(evidences[0].id)
    }
  }, [evidences])

  useEffect(() => {
    onChange(localEvidences)
  }, [localEvidences, onChange])

  const selectedEvidence = useMemo(
    () => localEvidences.find((evidence) => evidence.id === selectedTypeId) ?? localEvidences[0],
    [localEvidences, selectedTypeId]
  )

  const selectedTypeConfig = useMemo(
    () => (selectedEvidence ? EVIDENCE_TYPE_CONFIG[selectedEvidence.id] : EVIDENCE_TYPE_CONFIG.image),
    [selectedEvidence]
  )

  const isCommentType = selectedEvidence?.id === 'comment'
  const isSignType = selectedEvidence?.id === 'sign'

  const canvasToFile = async (canvas: HTMLCanvasElement, fileName: string): Promise<File> => {
    const dataUrl = canvas.toDataURL('image/png')
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    return new File([blob], fileName, { type: 'image/png' })
  }

  const filePreviewUrls = useMemo(() => {
    if (!selectedEvidence) return []
    return selectedEvidence.files.map((file) => {
      if (file.url) return file.url
      if (file.file) return URL.createObjectURL(file.file)
      return ''
    })
  }, [selectedEvidence?.files])

  useEffect(() => {
    return () => {
      filePreviewUrls.forEach((url) => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url)
        }
      })
    }
  }, [filePreviewUrls])

  const handleSelectType = (typeId: SolverEvidenceType) => {
    setSelectedTypeId(typeId)
    setCommentText('')
    setIsSignEmpty(true)
  }

  const createUploadingFile = useCallback(
    (file: File, evidenceTypeId: number): UploadedEvidenceFile => {
      const name = cleanFileName(file.name)
      const key = `${uploadNameRef.current}/${name}`
      return {
        name,
        url: '',
        key,
        evidenceTypeId,
        isUploading: true,
        file,
      }
    },
    []
  )

  const handleUploadResult = useCallback(
    (typeId: SolverEvidenceType, name: string, result: UploadedEvidenceFile | null) => {
      setLocalEvidences((prev) =>
        prev.map((evidence) => {
          if (evidence.id !== typeId) return evidence
          return {
            ...evidence,
            files: evidence.files.map((file) => {
              if (file.name !== name) return file
              if (result) {
                return {
                  ...result,
                  isUploading: false,
                  file: undefined,
                }
              }
              return {
                ...file,
                isUploading: false,
                error: t('actionPlan.manualCreation.evidences.uploadError'),
              }
            }),
          }
        })
      )
    },
    [t]
  )

  const uploadSingleFile = useCallback(
    async (file: File, typeId: SolverEvidenceType, uploadingFile: UploadedEvidenceFile) => {
      const promise = uploadFile(file, uploadingFile.evidenceTypeId, uploadNameRef.current)
        .then((result) => {
          handleUploadResult(typeId, uploadingFile.name, result)
        })
        .finally(() => {
          uploadPromisesRef.current = uploadPromisesRef.current.filter((p) => p !== promise)
        })

      uploadPromisesRef.current.push(promise)
      return promise
    },
    [uploadFile, handleUploadResult]
  )

  const getEvidenceCurrentCount = (evidence: SolverEvidence) =>
    evidence.id === 'comment' ? evidence.comments.length : evidence.files.length

  const isMaxAmountReached = useMemo(() => {
    if (!selectedEvidence || selectedEvidence.maxAmount <= 0) return false
    return getEvidenceCurrentCount(selectedEvidence) >= selectedEvidence.maxAmount
  }, [selectedEvidence])

  const handleAddFiles = useCallback(
    async (acceptedFiles: File[]) => {
      if (!selectedEvidence) return
      const typeId = selectedTypeId
      const evidenceTypeId = selectedEvidence.evidenceTypeId
      const currentCount = getEvidenceCurrentCount(selectedEvidence)
      const maxAmount = selectedEvidence.maxAmount

      const validFiles = acceptedFiles.filter((file) => {
        const validation = isValidEvidenceFile(file, selectedEvidence)
        return validation.valid
      })

      const invalidCount = acceptedFiles.length - validFiles.length
      if (invalidCount > 0) {
        showSnackbar(
          t('actionPlan.solveActionPlan.errors.invalidFilesDiscarded', { count: invalidCount })
        )
      }

      let filesToProcess = validFiles
      if (maxAmount > 0) {
        const availableSlots = maxAmount - currentCount
        if (availableSlots <= 0) {
          showSnackbar(
            t('actionPlan.solveActionPlan.errors.maxAmountExceeded', { count: maxAmount })
          )
          return
        }
        if (validFiles.length > availableSlots) {
          filesToProcess = validFiles.slice(0, availableSlots)
          showSnackbar(
            t('actionPlan.solveActionPlan.errors.maxAmountExceeded', { count: maxAmount })
          )
        }
      }

      if (filesToProcess.length === 0) return

      const uploadingFiles = filesToProcess.map((file) =>
        createUploadingFile(file, evidenceTypeId)
      )

      setLocalEvidences((prev) =>
        prev.map((evidence) => {
          if (evidence.id !== typeId) return evidence
          const currentFiles = selectedTypeConfig.multiple ? evidence.files : []
          return { ...evidence, files: [...currentFiles, ...uploadingFiles] }
        })
      )

      for (const [index, file] of filesToProcess.entries()) {
        await uploadSingleFile(file, typeId, uploadingFiles[index])
      }
    },
    [selectedEvidence, selectedTypeId, selectedTypeConfig.multiple, createUploadingFile, uploadSingleFile, t]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: isCommentType ? undefined : selectedTypeConfig.accept,
    multiple: selectedTypeConfig.multiple,
    onDrop: handleAddFiles,
    disabled: isCommentType || isMaxAmountReached,
  })

  const handleRemoveFile = useCallback(
    async (fileIndex: number) => {
      if (!selectedEvidence) return
      const file = selectedEvidence.files[fileIndex]
      if (!file) return

      if (file.url) {
        await deleteFile(file.key)
      }

      setLocalEvidences((prev) =>
        prev.map((evidence) => {
          if (evidence.id !== selectedTypeId) return evidence
          return {
            ...evidence,
            files: evidence.files.filter((_, index) => index !== fileIndex),
          }
        })
      )
    },
    [selectedEvidence, selectedTypeId, deleteFile]
  )

  const handleRetryUpload = useCallback(
    async (fileIndex: number) => {
      if (!selectedEvidence) return
      const file = selectedEvidence.files[fileIndex]
      if (!file?.file) return

      const typeId = selectedTypeId

      setLocalEvidences((prev) =>
        prev.map((evidence) => {
          if (evidence.id !== typeId) return evidence
          return {
            ...evidence,
            files: evidence.files.map((item, index) =>
              index === fileIndex ? { ...item, isUploading: true, error: undefined } : item
            ),
          }
        })
      )

      await uploadSingleFile(file.file, typeId, {
        ...file,
        isUploading: true,
        error: undefined,
      })
    },
    [selectedEvidence, selectedTypeId, uploadSingleFile]
  )

  const handleAddComment = () => {
    const trimmedComment = commentText.trim()
    if (!trimmedComment || !selectedEvidence) return
    if (selectedEvidence.maxAmount > 0 && selectedEvidence.comments.length >= selectedEvidence.maxAmount) {
      showSnackbar(
        t('actionPlan.solveActionPlan.errors.maxAmountExceeded', {
          maxAmount: selectedEvidence.maxAmount,
        })
      )
      return
    }
    setLocalEvidences((prev) =>
      prev.map((evidence) => {
        if (evidence.id !== selectedTypeId) return evidence
        return { ...evidence, comments: [...evidence.comments, trimmedComment] }
      })
    )
    setCommentText('')
  }

  const handleRemoveComment = (commentIndex: number) => {
    setLocalEvidences((prev) =>
      prev.map((evidence) => {
        if (evidence.id !== selectedTypeId) return evidence
        return {
          ...evidence,
          comments: evidence.comments.filter((_, index) => index !== commentIndex),
        }
      })
    )
  }

  const handleAddSignature = async () => {
    const canvas = signRef.current?.getCanvas()
    if (!canvas || isSignEmpty || !selectedEvidence) return
    if (selectedEvidence.maxAmount > 0 && selectedEvidence.files.length >= selectedEvidence.maxAmount) {
      showSnackbar(
        t('actionPlan.solveActionPlan.errors.maxAmountExceeded', {
          maxAmount: selectedEvidence.maxAmount,
        })
      )
      return
    }
    const file = await canvasToFile(canvas, `firma_${Date.now()}.png`)
    const typeId = selectedTypeId
    const uploadingFile = createUploadingFile(file, selectedEvidence.evidenceTypeId)

    setLocalEvidences((prev) =>
      prev.map((evidence) => {
        if (evidence.id !== typeId) return evidence
        return { ...evidence, files: [...evidence.files, uploadingFile] }
      })
    )

    signRef.current?.clear()
    setIsSignEmpty(true)

    await uploadSingleFile(file, typeId, uploadingFile)
  }

  const handleClearSignature = () => {
    signRef.current?.clear()
    setIsSignEmpty(true)
  }

  const getEvidenceCount = (evidence: SolverEvidence) => {
    const current = evidence.id === 'comment' ? evidence.comments.length : evidence.files.length
    if (evidence.maxAmount > 0) {
      return `${current}/${evidence.maxAmount}`
    }
    return `${current}`
  }

  const renderFileState = (file: UploadedEvidenceFile, variant: 'list' | 'grid' = 'list') => {
    const isGrid = variant === 'grid'

    if (file.isUploading) {
      return (
        <Box sx={isGrid ? styles.fileStateGrid : styles.fileState}>
          <CircularProgress size={16} />
          <Typography sx={styles.fileStateText}>
            {t('actionPlan.manualCreation.evidences.uploading')}
          </Typography>
        </Box>
      )
    }

    if (file.error) {
      return (
        <Box sx={isGrid ? styles.fileStateGridError : styles.fileStateError}>
          <Typography sx={styles.fileErrorText}>{file.error}</Typography>
        </Box>
      )
    }

    return null
  }

  const renderFileList = () => {
    if (!selectedEvidence) return null

    if (viewMode === 'grid') {
      const isDocumentType = isDocumentEvidence(selectedEvidence.files[0]?.evidenceTypeId ?? 0)

      return (
        <Box sx={styles.filesGrid}>
          {selectedEvidence.files.map((file, index) => (
            <Box
              key={`${file.name}-${index}`}
              sx={[
                isDocumentType ? styles.fileCardGridDocument : styles.fileCardGrid,
                file.error ? styles.fileCardError : {},
              ]}
            >
              {file.isUploading || file.error ? (
                <>
                  {renderFileState(file, 'grid')}
                  <Box sx={styles.fileGridActionsOverlay}>
                    {file.error && (
                      <IconButton
                        sx={styles.retryButton}
                        onClick={() => handleRetryUpload(index)}
                        aria-label={t('actionPlan.manualCreation.evidences.retry')}
                      >
                        {icons('refresh')}
                      </IconButton>
                    )}
                    {!file.isUploading && (
                      <IconButton
                        sx={styles.deleteButtonGrid}
                        onClick={() => handleRemoveFile(index)}
                        aria-label={t('general.delete')}
                      >
                        {icons('delete')}
                      </IconButton>
                    )}
                  </Box>
                </>
              ) : isImageEvidence(file.evidenceTypeId) ? (
                <>
                  <Box
                    component='img'
                    src={filePreviewUrls[index]}
                    alt={file.name}
                    sx={styles.filePreviewGrid}
                  />
                  <Box sx={styles.filePreviewGridOverlay} data-overlay='true'>
                    <IconButton
                      sx={styles.deleteButtonGridHover}
                      onClick={(event) => {
                        event.stopPropagation()
                        handleRemoveFile(index)
                      }}
                      aria-label={t('general.delete')}
                      disableRipple
                    >
                      {icons('delete')}
                    </IconButton>
                  </Box>
                </>
              ) : isVideoEvidence(file.evidenceTypeId) ? (
                <>
                  <Box
                    component='video'
                    src={filePreviewUrls[index]}
                    preload='metadata'
                    muted
                    aria-label={file.name}
                    sx={styles.filePreviewGrid}
                  />
                  <Box sx={styles.filePreviewGridOverlay} data-overlay='true'>
                    <IconButton
                      sx={styles.deleteButtonGridHover}
                      onClick={(event) => {
                        event.stopPropagation()
                        handleRemoveFile(index)
                      }}
                      aria-label={t('general.delete')}
                      disableRipple
                    >
                      {icons('delete')}
                    </IconButton>
                  </Box>
                </>
              ) : isDocumentEvidence(file.evidenceTypeId) ? (
                <>
                  <Box sx={styles.fileIconDocument}>{icons('paper')}</Box>
                  <Box sx={styles.fileNameWithAction}>
                    <Typography sx={styles.fileNameGrid} title={file.name}>
                      {file.name}
                    </Typography>
                    <IconButton
                      sx={styles.deleteButtonGrid}
                      onClick={() => handleRemoveFile(index)}
                      aria-label={t('general.delete')}
                    >
                      {icons('delete')}
                    </IconButton>
                  </Box>
                </>
              ) : null}
            </Box>
          ))}
        </Box>
      )
    }

    return (
      <Box sx={styles.filesList}>
        {selectedEvidence.files.map((file, index) => (
          <Box
            key={`${file.name}-${index}`}
            sx={[styles.fileCardList, file.error ? styles.fileCardListError : {}]}
          >
            <Typography sx={styles.fileNameList} title={file.name}>
              {file.name}
            </Typography>
            {renderFileState(file, 'list')}
            <Box sx={styles.fileActions}>
              {file.error && (
                <IconButton
                  sx={styles.retryButton}
                  onClick={() => handleRetryUpload(index)}
                  aria-label={t('actionPlan.manualCreation.evidences.retry')}
                >
                  {icons('refresh')}
                </IconButton>
              )}
              {!file.isUploading && (
                <IconButton
                  sx={styles.deleteButton}
                  onClick={() => handleRemoveFile(index)}
                  aria-label={t('general.delete')}
                >
                  {icons('delete')}
                </IconButton>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  const renderCommentsList = () => (
    <Box sx={styles.commentsList}>
      {selectedEvidence?.comments.map((comment, index) => (
        <Box key={`comment-${index}`} sx={styles.commentCard}>
          <Box sx={styles.commentIcon}>{icons('chatBubbleOutlineOutlined')}</Box>
          <Typography sx={styles.commentText}>{comment}</Typography>
          <IconButton
            sx={styles.deleteButton}
            onClick={() => handleRemoveComment(index)}
            aria-label={t('general.delete')}
          >
            {icons('delete')}
          </IconButton>
        </Box>
      ))}
    </Box>
  )

  const renderRightContent = () => {
    if (!selectedEvidence) {
      return (
        <Box sx={styles.emptyState}>
          <Typography sx={styles.emptyStateText}>
            {t('actionPlan.manualCreation.evidences.noInformation')}
          </Typography>
        </Box>
      )
    }

    if (isCommentType) {
      return (
        <Box sx={styles.rightContent}>
          <Box sx={styles.commentInput}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              placeholder={t('actionPlan.manualCreation.evidences.commentPlaceholder')}
              value={commentText}
              onChange={(event) => setCommentText(event.target.value)}
              sx={styles.commentTextField}
              variant='outlined'
            />
            <Box
              role='button'
              tabIndex={0}
              aria-disabled={!commentText.trim() || isMaxAmountReached}
              sx={[
                styles.commentAddButton,
                (!commentText.trim() || isMaxAmountReached) && styles.commentAddButtonDisabled,
              ]}
              onClick={() => {
                if (commentText.trim() && !isMaxAmountReached) handleAddComment()
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  if (commentText.trim() && !isMaxAmountReached) handleAddComment()
                }
              }}
            >
              {t('actionPlan.manualCreation.evidences.addComment')}
            </Box>
          </Box>
          <Box sx={styles.commentsContainer}>
            {selectedEvidence.comments.length > 0 && renderCommentsList()}
          </Box>
        </Box>
      )
    }

    if (isSignType) {
      return (
        <Box sx={styles.rightContent}>
          <Box sx={styles.signatureInput}>
            <Box sx={styles.signatureCanvas}>
              <SignatureCanvas
                ref={signRef}
                penColor='#000'
                backgroundColor='#fff'
                onBegin={() => setIsSignEmpty(false)}
                canvasProps={{ className: 'sigCanvas' }}
              />
            </Box>
            <Box sx={styles.signatureActions}>
              <Box
                role='button'
                tabIndex={0}
                aria-disabled={isSignEmpty}
                sx={[
                  styles.signatureClearButton,
                  isSignEmpty && styles.signatureClearButtonDisabled,
                ]}
                onClick={() => {
                  if (!isSignEmpty) handleClearSignature()
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    if (!isSignEmpty) handleClearSignature()
                  }
                }}
              >
                {t('actionPlan.manualCreation.evidences.clearSignature')}
              </Box>
              <Box
                role='button'
                tabIndex={0}
                aria-disabled={isSignEmpty || isMaxAmountReached}
                sx={[
                  styles.signatureAddButton,
                  (isSignEmpty || isMaxAmountReached) && styles.signatureAddButtonDisabled,
                ]}
                onClick={() => {
                  if (!isSignEmpty && !isMaxAmountReached) handleAddSignature()
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    if (!isSignEmpty && !isMaxAmountReached) handleAddSignature()
                  }
                }}
              >
                {t('actionPlan.manualCreation.evidences.addSignature')}
              </Box>
            </Box>
          </Box>

          <Box sx={styles.filesContainer}>
            {selectedEvidence.files.length > 0 ? (
              <Box sx={styles.filesSection}>{renderFileList()}</Box>
            ) : (
              <Box sx={styles.emptyState}>
                <Typography sx={styles.emptyStateText}>
                  {t('actionPlan.solveActionPlan.noFilesAttached')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )
    }

    return (
      <Box sx={styles.rightContent}>
        <Box sx={styles.dropzoneWrapper}>
          <Box
            {...getRootProps()}
            sx={[
              styles.dropzone,
              isDragActive && styles.dropzoneActive,
              isMaxAmountReached && styles.dropzoneDisabled,
            ]}
          >
            <input {...getInputProps()} />
            <Box sx={styles.dropzoneContent}>
              <Box sx={styles.dropzoneIcon}>{icons('uploadFile')}</Box>
              <Typography sx={styles.dropzoneText}>
                {t('actionPlan.manualCreation.evidences.dropzoneTitle')}
              </Typography>
            </Box>
            <Typography sx={styles.dropzoneFormats}>
              {t('actionPlan.manualCreation.evidences.allowedFormats', {
                formats:
                  selectedEvidence.allowedTypes.length > 0
                    ? selectedEvidence.allowedTypes.join(', ').toUpperCase()
                    : selectedTypeConfig.formats,
              })}
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.filesContainer}>
          {selectedEvidence.files.length > 0 ? (
            <Box sx={styles.filesSection}>{renderFileList()}</Box>
          ) : (
            <Box sx={styles.emptyState}>
              <Typography sx={styles.emptyStateText}>
                {t('actionPlan.solveActionPlan.noFilesAttached')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  if (localEvidences.length === 0) {
    return (
      <Box sx={styles.emptyState}>
        <Typography sx={styles.emptyStateText}>
          {t('actionPlan.manualCreation.evidences.noInformation')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={styles.uploaderContainer}>
      <Box sx={styles.infoBox}>
        <Typography sx={styles.subtitle}>
          {t('actionPlan.solveActionPlan.evidenceLoadTitle')}
        </Typography>
        <Typography sx={styles.description}>
          {t('actionPlan.solveActionPlan.evidenceLoadSubtitle')}
        </Typography>
      </Box>

      <Box sx={styles.content}>
        <Box sx={styles.leftPanel}>
          <Typography sx={styles.panelHeader}>
            {t('actionPlan.manualCreation.evidences.panelTitle')}
          </Typography>
          <Box sx={styles.typesList}>
            {localEvidences.map((evidence) => {
              const isSelected = evidence.id === selectedTypeId
              return (
                <Box
                  key={evidence.id}
                  role='button'
                  aria-pressed={isSelected}
                  tabIndex={0}
                  onClick={() => handleSelectType(evidence.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleSelectType(evidence.id)
                    }
                  }}
                  sx={[styles.typeRow, isSelected && styles.typeRowActive]}
                >
                  <Typography sx={styles.typeLabel}>
                    {t(evidence.label)}
                    {evidence.required && (
                      <Box component='span' sx={styles.requiredIndicator}>
                        {' '}
                        *
                      </Box>
                    )}
                  </Typography>
                  <Typography sx={styles.typeCount}>{getEvidenceCount(evidence)}</Typography>
                </Box>
              )
            })}
          </Box>
        </Box>

        <Box sx={styles.rightPanel}>
          <Box sx={styles.rightHeader}>
            <Typography sx={styles.rightTitle}>{selectedEvidence ? t(selectedEvidence.label) : ''}</Typography>
            {!isCommentType && (
              <Box sx={styles.viewToggle}>
                <Box
                  role='button'
                  tabIndex={0}
                  sx={[styles.toggleButton, viewMode === 'list' && styles.toggleButtonActive]}
                  onClick={() => setViewMode('list')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setViewMode('list')
                    }
                  }}
                  aria-pressed={viewMode === 'list'}
                >
                  {icons('list')}
                  <Typography sx={styles.toggleLabel}>
                    {t('actionPlan.manualCreation.evidences.list')}
                  </Typography>
                </Box>
                <Box
                  role='button'
                  tabIndex={0}
                  sx={[styles.toggleButton, viewMode === 'grid' && styles.toggleButtonActive]}
                  onClick={() => setViewMode('grid')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setViewMode('grid')
                    }
                  }}
                  aria-pressed={viewMode === 'grid'}
                >
                  {icons('mosaic')}
                  <Typography sx={styles.toggleLabel}>
                    {t('actionPlan.manualCreation.evidences.grid')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {renderRightContent()}
        </Box>
      </Box>

      <SnackSimple
        open={snackbar.open}
        close={handleCloseSnackbar}
        message={snackbar.message}
        type={snackbar.type}
      />
    </Box>
  )
}

export default EvidenceUploader
